import { z } from 'zod';
import { hasKey, type EConditionalOperators } from '@hubertai/screening-question-types';

export enum EEvaluationType {
  Bonus = 'bonus',
  Warning = 'warning',
  Qualified = 'qualified',
  Disqualified = 'disqualified',
  Weighted = 'weighted',
  Point = 'point',
  Info = 'info',
  AutoQualified = 'auto_qualified',
}

export type TEvaluation = {
  id?: string;
  name: EEvaluationType;
  bonus_points?: number;
  points?: number;
  weight?: number;
  weighted?: boolean;
  isDefault?: boolean;
};

type TOpenEvaluation = {
  id: string;
  scoring_category: string;
  weight: number;
  parentIds?: string;
};

export type TCalendarEvaluation = {
  name: EEvaluationType;
  bonus_points?: number;
  condition: {
    type: 'relative' | 'absolute';
    reference: 'answer';
    operator: EConditionalOperators;
    value: string;
  };
};

export type TDatePickerEvaluationSummary = {
  id: string;
  summary: TEvaluationSummary;
  evaluation: TCalendarEvaluation[];
};

export type TEvaluationSummary = {
  [EEvaluationType.Bonus]: number;
  [EEvaluationType.Warning]: number;
  [EEvaluationType.Disqualified]: number;
  [EEvaluationType.Info]: number;
  [EEvaluationType.Weighted]?: number;
  [EEvaluationType.AutoQualified]?: number;
};

export type TSelectEvaluationSummary = {
  id: string;
  summary: TEvaluationSummary;
  evaluation?: {
    [key: string]: TEvaluation | string | undefined;
    scoring_category?: string;
  };
  isDefault: boolean;
};

export type TOpenEvaluationSummary = {
  id: string;
  summary: TEvaluationSummary;
  evaluation: TOpenEvaluation;
  intentEvaluation: {
    [key: string]: TEvaluation;
  };
  isDefault: boolean;
};

// Slider Evaluation
export enum ESliderEvaluationType {
  Weighted = 'weighted',
  Conditional = 'conditional',
}

export type TSliderEvaluationConditional = {
  name: EEvaluationType;
  bonus_points?: number;
  condition: {
    type: 'relative' | 'absolute';
    reference: 'answer';
    operator:
      | EConditionalOperators.Equals
      | EConditionalOperators.GreaterThan
      | EConditionalOperators.LessThan
      | EConditionalOperators.Between;
    value: number[];
  };
};

// Slider Evaluation
export enum EMatrixEvaluationType {
  Weighted = 'weighted',
  Conditional = 'conditional',
}
export type TMatrixEvaluationConditional = {
  name: EEvaluationType;
  bonus_points?: number;
  condition: {
    operator:
      | EConditionalOperators.Equals
      | EConditionalOperators.GreaterThan
      | EConditionalOperators.LessThan
      | EConditionalOperators.Between;
    value: {
      row_id?: string;
      column_id?: string;
      value?: string | number;
    };
  };
};

export const zMatrixSliderEvaluationWeighted = z.object({
  weight: z
    .number()
    .min(0, 'Slider/matrix weight must be between 0 and 1.')
    .max(1, 'Slider/matrix weight must be between 0 and 1.'),
  policy: z.literal('basic'),
});
export type TMatrixSliderEvaluationWeighted = z.infer<typeof zMatrixSliderEvaluationWeighted>;

export type TFormSliderEvaluationSummary = {
  id: string;
  summary: TEvaluationSummary;
  // Evaluation by form ID
  evaluation: {
    [key: string]: TSliderEvaluationConditional[] | TMatrixSliderEvaluationWeighted;
  };
  isDefault: boolean;
};

export function isWeightedEvaluation(
  evaluation: TMatrixEvaluationConditional[] | TSliderEvaluationConditional[] | TMatrixSliderEvaluationWeighted,
): evaluation is TMatrixSliderEvaluationWeighted {
  return evaluation && hasKey(evaluation, 'weight');
}

export function isConditionalEvaluation(
  evaluation: TSliderEvaluationConditional[] | TMatrixSliderEvaluationWeighted,
): evaluation is TSliderEvaluationConditional[] {
  return evaluation && Array.isArray(evaluation);
}

export function isMatrixConditionalEvaluation(
  evaluation: TMatrixEvaluationConditional[] | TMatrixSliderEvaluationWeighted,
): evaluation is TMatrixEvaluationConditional[] {
  return evaluation && Array.isArray(evaluation);
}
